import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ModalService } from '../../services/modal/modal.service';
import { ComponentDataDestService } from '../../services/component-data-dest/component-data-dest.service';
import { environment } from '../../environment/environment';

export const EDIT_PATH_USER_MODAL_KEY = 'edit_path_user_modal';
export const EDIT_PATH_USER_MODAL_DATA = Symbol('EDIT_PATH_USER_MODAL_DATA');

export interface EditPathUserModalData {
  pathGuid: string;
  userUuid: string;
  full_name?: string;
  requirement_started_at?: string;
  maintenance_started_at?: string;
  version_id?: number | null;
}

@Component({
  selector: 'app-edit-path-user-modal',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './edit-path-user-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./edit-path-user-modal.component.scss'],
})
export class EditPathUserModalComponent implements OnInit {
  full_name = '';
  requirement_started_at = '';
  maintenance_started_at = '';
  version_id: number | null = null;
  removeUser = false;
  versions: { version_id: number; name: string }[] = [];
  loadingVersions = true;
  submitting = false;
  error: string | null = null;

  private pathGuid = '';
  private userUuid = '';

  constructor(
    @Inject(EDIT_PATH_USER_MODAL_DATA) public data: EditPathUserModalData,
    private modalService: ModalService,
    private dataDestService: ComponentDataDestService,
    private http: HttpClient
  ) {
    this.pathGuid = data.pathGuid ?? '';
    this.userUuid = data.userUuid ?? '';
    this.full_name = data.full_name ?? '';
    this.requirement_started_at = data.requirement_started_at ?? '';
    this.maintenance_started_at = data.maintenance_started_at ?? '';
    this.version_id = data.version_id ?? null;
  }

  ngOnInit(): void {
    this.loadVersions();
  }

  private loadVersions(): void {
    if (!this.pathGuid) {
      this.loadingVersions = false;
      return;
    }
    this.loadingVersions = true;
    this.http
      .get<unknown[]>(`${environment.apiUrl}/paths/${this.pathGuid}/versions`, {
        withCredentials: true,
      })
      .subscribe({
        next: (result) => {
          this.versions = (result || [])
            .map((v: any) => ({
              version_id: v.version_id ?? v.id ?? 0,
              name:
                v.display_name ?? v.name ?? String(v.version_id ?? v.id ?? ''),
            }))
            .filter((v) => v.version_id);
          if (
            this.versions.length &&
            this.version_id === null &&
            this.versions[0]
          ) {
            this.version_id = this.versions[0].version_id;
          }
          this.loadingVersions = false;
        },
        error: () => {
          this.versions = [];
          this.loadingVersions = false;
        },
      });
  }

  close(): void {
    this.modalService.closeModal(EDIT_PATH_USER_MODAL_KEY);
  }

  submit(): void {
    this.error = null;
    if (!this.pathGuid || !this.userUuid) {
      this.error = 'Manglende sti eller bruger.';
      return;
    }
    if (!this.removeUser && !this.version_id) {
      this.error = 'Vælg en version.';
      return;
    }
    this.submitting = true;
    const payload = {
      pathGuid: this.pathGuid,
      userUuid: this.userUuid,
      full_name: this.full_name,
      requirement_started_at: this.requirement_started_at || undefined,
      maintenance_started_at: this.maintenance_started_at || undefined,
      version_id: this.removeUser ? undefined : this.version_id,
      removeUser: this.removeUser,
    };
    const dest = this.dataDestService.getDataDest('update_path_user');
    if (!dest) {
      this.error = 'Handlingen er ikke tilgængelig.';
      this.submitting = false;
      return;
    }
    const result$ = dest(payload);
    if (result$ && typeof result$.subscribe === 'function') {
      result$.subscribe({
        next: () => this.close(),
        error: (err: unknown) => {
          this.error =
            (err instanceof Error ? err.message : null) ??
            'Der opstod en fejl.';
          this.submitting = false;
        },
      });
    } else {
      this.submitting = false;
      this.close();
    }
  }
}
